import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControlLabel, IconButton,
  Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Stack,
  MenuItem, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import AddCommentRoundedIcon from '@mui/icons-material/AddCommentRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import TheaterComedyRoundedIcon from '@mui/icons-material/TheaterComedyRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';

import MicTranscribeButton from '../components/MicTranscribeButton';
import ConversationStorageControls from '../components/ConversationStorageControls';
import {
  BuddyWorkspace, ConversationScaffoldAids, ConversationTimeline, EndConversationControl, WorkspaceComposerArea, WorkspaceHeader,
} from '../components/buddy-workspace';
import { FASTAPI_HOST } from '../settings/config';
import { DEFAULT_BUDDY } from '../settings/buddyProfiles';
import { STORY_LANGUAGES } from '../settings/staticData';
import {
  actionHasSpokenReply, isScenarioCoachMessage, newAssistantText, scenarioCoachText,
} from './ScenarioPracticeUtils';


const TRANSLATION_KEYS = {
  title: 'scenario_practice', subtitle: 'scenario_practice_subtitle', placeholder: 'scenario_practice_placeholder',
  send: 'scenario_practice_send', end: 'scenario_practice_end', restart: 'scenario_practice_restart',
  hint: 'scenario_practice_hint', optionsOn: 'scenario_practice_options_on', optionsOff: 'scenario_practice_options_off',
  settings: 'scenario_practice_settings', role: 'scenario_practice_role', temporary: 'scenario_practice_intro',
  coach: 'scenario_practice_coach', createScenario: 'scenario_practice_create', createTitle: 'scenario_practice_create_title',
  describe: 'scenario_practice_describe', describePlaceholder: 'scenario_practice_describe_placeholder',
  setting: 'scenario_practice_setting', objective: 'scenario_practice_objective',
  storyObjective: 'scenario_practice_story_objective', minimumTurns: 'scenario_practice_minimum_turns',
  yourRole: 'scenario_practice_your_role', buddyRole: 'scenario_practice_buddy_role',
  referenceDetails: 'scenario_practice_reference_details', referencePlaceholder: 'scenario_practice_reference_placeholder',
  cancel: 'scenario_practice_cancel', buildAndStart: 'scenario_practice_build_start', building: 'scenario_practice_building',
  buildError: 'scenario_practice_build_error', temporaryCustom: 'scenario_practice_temporary',
  addReferences: 'scenario_practice_add_references', referencesHelp: 'scenario_practice_references_help',
  videoLater: 'scenario_practice_video_later', removeReference: 'scenario_practice_remove_reference',
  sendError: 'scenario_practice_send_error', voiceOn: 'scenario_practice_voice_on', voiceOff: 'scenario_practice_voice_off',
  voiceError: 'scenario_practice_voice_error', checkingPlan: 'workspace_checking_plan', paidRequired: 'workspace_paid_required',
  languageFeedback: 'scenario_practice_language_feedback',
  languageSuggestion: 'scenario_practice_language_suggestion', summaryTitle: 'scenario_practice_summary_title',
  summaryEvidence: 'scenario_practice_summary_evidence', summaryNext: 'scenario_practice_summary_next',
  reviewTitle: 'scenario_practice_review_title', reviewIntro: 'scenario_practice_review_intro',
  planScenario: 'scenario_practice_plan', reviewGoals: 'scenario_practice_review_goals',
  opening: 'scenario_practice_opening', saveChanges: 'scenario_practice_save_changes',
  conversationLanguage: 'scenario_practice_conversation_language',
  startPractice: 'scenario_practice_start_reviewed',
  capabilityHelp: 'scenario_practice_capability_help', capabilityAccept: 'scenario_practice_capability_accept',
  planExpired: 'scenario_practice_plan_expired',
  goalCore: 'scenario_practice_goal_core', goalHelpful: 'scenario_practice_goal_helpful',
  goalOptional: 'scenario_practice_goal_optional',
  addGoal: 'scenario_practice_add_goal', removeGoal: 'scenario_practice_remove_goal',
  goalPlaceholder: 'scenario_practice_goal_placeholder', coreGoalRequired: 'scenario_practice_core_goal_required',
};

const copyFor = (translations = {}) => {
  const localized = Object.fromEntries(
    Object.entries(TRANSLATION_KEYS).map(([property, key]) => [property, translations[key] || '']),
  );
  return {
    ...localized,
    examples: [
    {
      label: translations.scenario_practice_example_school || '',
      description: translations.scenario_practice_example_school_desc || '',
      setting: translations.scenario_practice_example_school_setting || '',
      objective: translations.scenario_practice_example_school_objective || '',
    },
    {
      label: translations.scenario_practice_example_game || '',
      description: translations.scenario_practice_example_game_desc || '',
      setting: translations.scenario_practice_example_game_setting || '',
      objective: translations.scenario_practice_example_game_objective || '',
    },
    {
      label: translations.scenario_practice_example_shop || '',
      description: translations.scenario_practice_example_shop_desc || '',
      setting: translations.scenario_practice_example_shop_setting || '',
      objective: translations.scenario_practice_example_shop_objective || '',
    },
    ],
  };
};
const errorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return (typeof detail === 'string' ? detail : detail?.message) || error?.message || fallback;
};

export default function ScenarioPracticePage({
  currentLang = 'English', childName = '', childAge = 8,
  roleLanguage = 'English',
  conversationLevel = 'intermediate',
  paidAccess = false, planChecked = false, translations = {},
  onPurchase,
  initialPractice = null,
}) {
  const text = copyFor(translations);
  const endRef = useRef(null);
  const referenceInputRef = useRef(null);
  const audioRef = useRef(null);
  const audioUrlRef = useRef('');
  const ttsAbortRef = useRef(null);
  const [sessionId, setSessionId] = useState('');
  const [version, setVersion] = useState(0);
  const [messages, setMessages] = useState([]);
  const [choices, setChoices] = useState([]);
  const [hint, setHint] = useState('');
  const [status, setStatus] = useState('collecting');
  const [answer, setAnswer] = useState('');
  const [showOptions, setShowOptions] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [builderOpen, setBuilderOpen] = useState(false);
  const [building, setBuilding] = useState(false);
  const [builderStep, setBuilderStep] = useState('input');
  const [scenarioPlan, setScenarioPlan] = useState(null);
  const [reviewDraft, setReviewDraft] = useState(null);
  const [scenarioTitle, setScenarioTitle] = useState('');
  const [scenarioSubtitle, setScenarioSubtitle] = useState('');
  const [scenarioRole, setScenarioRole] = useState('');
  const [coachGuidance, setCoachGuidance] = useState('');
  const [practiceSummary, setPracticeSummary] = useState(null);
  const [languageFeedback, setLanguageFeedback] = useState({});
  const [visibleLanguageFeedback, setVisibleLanguageFeedback] = useState({});
  const [conversationLanguage, setConversationLanguage] = useState(roleLanguage || 'English');
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [voicePlaying, setVoicePlaying] = useState(false);
  const [buddyVoice, setBuddyVoice] = useState(DEFAULT_BUDDY.voice || 'ai_01');
  const [speechSpeed, setSpeechSpeed] = useState(1);
  const [storageMode, setStorageMode] = useState('temporary');
  const [referenceFiles, setReferenceFiles] = useState([]);
  const [scenarioForm, setScenarioForm] = useState({
    description: '', setting: '', objective: '', participantRole: '',
    assistantRole: '', referenceDetails: '', audience: 'child', supportLevel: 'supported', minimumTurns: 3,
  });

  useEffect(() => {
    if (!initialPractice?.title && !initialPractice?.description) return;
    const title = String(initialPractice.title || '').trim();
    const detail = String(initialPractice.description || '').trim();
    setScenarioForm((current) => ({
      ...current,
      description: title || detail,
      objective: title ? text.storyObjective.replace('{title}', title) : current.objective,
      referenceDetails: detail,
    }));
    setScenarioTitle(title);
    setError('');
    setBuilderStep('input');
    setScenarioPlan(null);
    setBuilderOpen(true);
  }, [initialPractice, text.storyObjective]);

  const authConfig = () => ({
    headers: { Authorization: `Bearer ${Cookies.get('user_token') || ''}` },
  });
  const syncOptionsPreference = async (view, enabled, language = conversationLanguage) => {
    if (!view?.session_id || Boolean(view?.context?.coach_enabled) === enabled) return view;
    const { data } = await axios.post(`${FASTAPI_HOST}/api/conversations/${view.session_id}/actions`, {
      request_id: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
      expected_version: Number(view.version) || 0,
      type: 'set_coach_enabled',
      value: enabled,
      conversation_language: language,
    }, authConfig());
    return data;
  };
  const applyView = (view) => {
    if (view?.context?.persistence_warning) setError(view.context.persistence_warning);
    setSessionId(view?.session_id || '');
    setStorageMode(view?.context?.storage_mode || 'temporary');
    setVersion(Number(view?.version) || 0);
    setMessages(Array.isArray(view?.messages) ? view.messages : []);
    setChoices(Array.isArray(view?.input?.choices) ? view.input.choices : []);
    setHint(String(view?.input?.hint || ''));
    setCoachGuidance(String(view?.context?.coach_guidance || ''));
    setPracticeSummary(view?.context?.practice_summary || null);
    setLanguageFeedback(view?.context?.language_feedback_by_message || {});
    setShowOptions(Boolean(view?.context?.coach_enabled));
    setStatus(view?.status || 'collecting');
    if (view?.context?.title) setScenarioTitle(String(view.context.title));
    if (view?.context?.objective) setScenarioSubtitle(String(view.context.objective));
    if (view?.context?.assistant_role) {
      setScenarioRole(String(view.context.assistant_role).replaceAll('_', ' '));
    }
  };
  const latestAssistantText = (items = messages) => {
    for (let index = items.length - 1; index >= 0; index -= 1) {
      if (items[index]?.role === 'assistant' && !isScenarioCoachMessage(items[index])) {
        return String(items[index]?.content || '').trim();
      }
    }
    return '';
  };
  const stopSpeaking = () => {
    if (ttsAbortRef.current) ttsAbortRef.current.abort();
    ttsAbortRef.current = null;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    audioRef.current = null;
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
    audioUrlRef.current = '';
    setVoicePlaying(false);
  };
  const speakText = async (value) => {
    const content = String(value || '').trim();
    if (!content) return;
    stopSpeaking();
    setVoicePlaying(true);
    const controller = new AbortController();
    ttsAbortRef.current = controller;
    try {
      const response = await axios.post(
        `${FASTAPI_HOST}/api/photo-story/tts`,
        { text: content, voice: buddyVoice, language: conversationLanguage || 'English' },
        { ...authConfig(), responseType: 'arraybuffer', signal: controller.signal },
      );
      const url = URL.createObjectURL(new Blob([response.data], { type: 'audio/mpeg' }));
      const audio = new Audio(url);
      audio.playbackRate = Number(speechSpeed) || 1;
      audio.preservesPitch = true;
      audio.mozPreservesPitch = true;
      audio.webkitPreservesPitch = true;
      audioRef.current = audio;
      audioUrlRef.current = url;
      const release = () => {
        if (audioRef.current === audio) audioRef.current = null;
        if (audioUrlRef.current === url) {
          URL.revokeObjectURL(url);
          audioUrlRef.current = '';
        }
        setVoicePlaying(false);
      };
      audio.onended = release;
      audio.onerror = release;
      await audio.play();
    } catch (requestError) {
      if (requestError?.code !== 'ERR_CANCELED') setError(text.voiceError);
      stopSpeaking();
    }
  };
  const chooseExample = (example) => {
    setScenarioForm((current) => ({
      ...current,
      description: example.description,
      setting: example.setting,
      objective: example.objective,
      participantRole: '',
      assistantRole: '',
      referenceDetails: '',
    }));
    setError('');
    setBuilderStep('input');
    setScenarioPlan(null);
    setBuilderOpen(true);
  };
  const planCustomScenario = async () => {
    if (!scenarioForm.description.trim() || building) return;
    stopSpeaking();
    setBuilding(true); setError('');
    try {
      const normalizedLevel = String(conversationLevel || 'intermediate').toLowerCase();
      const request = {
        description: scenarioForm.description.trim(), setting: scenarioForm.setting.trim(),
        objective: scenarioForm.objective.trim(), participant_role: scenarioForm.participantRole.trim(),
        assistant_role: scenarioForm.assistantRole.trim(), reference_details: scenarioForm.referenceDetails.trim(),
        // audience: scenarioForm.audience,
        // support_level: scenarioForm.supportLevel,
        minimum_turns: 3,
        // Build the review plan in the UI language. The independently selected
        // conversation language is applied only when the reviewed plan starts.
        language: currentLang || 'English',
        child_name: childName || '', child_age: Number(childAge) || 8,
        conversation_level: ['basic', 'intermediate', 'advanced'].includes(normalizedLevel) ? normalizedLevel : 'intermediate',
      };
      const response = await axios.post(
        `${FASTAPI_HOST}/api/conversations/scenarios/plans`, request, authConfig(),
      );
      const { data } = response;
      const definition = data?.definition || {};
      setScenarioPlan(data);
      setReviewDraft({
        language: roleLanguage || data?.language || 'English',
        title: definition.title || '', setting: definition.setting || '',
        objective: definition.objective || '', participant_role: definition.participant_role || '',
        assistant_role: definition.assistant_role || '', opening_message: definition.opening_message || '',
        fallback_hint: definition.fallback_hint || '',
        goals: Array.isArray(definition.goals) ? definition.goals : [],
      });
      setBuilderStep('review');
    } catch (requestError) {
      setError(errorMessage(requestError, text.buildError));
    } finally { setBuilding(false); }
  };
  const startReviewedScenario = async () => {
    if (!scenarioPlan?.plan_id || !reviewDraft || building) return;
    setBuilding(true); setError('');
    try {
      const updated = await axios.patch(
        `${FASTAPI_HOST}/api/conversations/scenarios/plans/${scenarioPlan.plan_id}`,
        { expected_revision: scenarioPlan.revision, ...reviewDraft },
        authConfig(),
      );
      const currentPlan = updated.data;
      setScenarioPlan(currentPlan);
      const { data } = await axios.post(
        `${FASTAPI_HOST}/api/conversations/scenarios/plans/${currentPlan.plan_id}/start`,
        { expected_revision: currentPlan.revision },
        authConfig(),
      );
      const selectedLanguage = reviewDraft.language || roleLanguage || 'English';
      const synchronizedView = await syncOptionsPreference(data, showOptions, selectedLanguage);
      setConversationLanguage(selectedLanguage);
      applyView(synchronizedView);
      setScenarioTitle(synchronizedView?.context?.title || reviewDraft.title || text.title);
      setScenarioSubtitle(synchronizedView?.context?.objective || reviewDraft.objective || text.subtitle);
      setScenarioRole(String(synchronizedView?.context?.assistant_role || reviewDraft.assistant_role || text.role).replaceAll('_', ' '));
      setBuilderOpen(false); setBuilderStep('input'); setScenarioPlan(null);
      setReviewDraft(null); setReferenceFiles([]);
    } catch (requestError) {
      const statusCode = requestError?.response?.status;
      setError(statusCode === 404 ? text.planExpired : errorMessage(requestError, text.buildError));
    } finally { setBuilding(false); }
  };
  const addReviewGoal = () => {
    setReviewDraft((current) => {
      if (!current || current.goals.length >= 6) return current;
      const existing = new Set(current.goals.map((goal) => goal.id));
      let index = current.goals.length + 1;
      let id = `custom_goal_${index}`;
      while (existing.has(id)) { index += 1; id = `custom_goal_${index}`; }
      return {
        ...current,
        goals: [...current.goals, { id, description: '', importance: 'helpful' }],
      };
    });
  };
  const removeReviewGoal = (index) => {
    setReviewDraft((current) => {
      if (!current || current.goals.length <= 1) return current;
      return { ...current, goals: current.goals.filter((_, itemIndex) => itemIndex !== index) };
    });
  };
  const restartScenario = async () => {
    if (!sessionId || building) return;
    stopSpeaking();
    setBuilding(true); setError(''); setAnswer('');
    try {
      const { data } = await axios.post(
        `${FASTAPI_HOST}/api/conversations/scenarios/${sessionId}/restart`,
        {},
        authConfig(),
      );
      applyView(data);
      setScenarioTitle(data?.context?.title || scenarioTitle || text.title);
      setScenarioSubtitle(data?.context?.objective || scenarioSubtitle || text.subtitle);
      setScenarioRole(String(data?.context?.assistant_role || scenarioRole || text.role).replaceAll('_', ' '));
    } catch (requestError) {
      setError(errorMessage(requestError, text.buildError));
    } finally {
      setBuilding(false);
    }
  };
  const sendAction = async (type, value = '') => {
    if (!sessionId || submitting) return;
    stopSpeaking();
    setSubmitting(true); setError('');
    try {
      const { data } = await axios.post(`${FASTAPI_HOST}/api/conversations/${sessionId}/actions`, {
        request_id: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
        expected_version: version, type, value, conversation_language: conversationLanguage,
      }, authConfig());
      applyView(data); setAnswer('');
      if (voiceEnabled && actionHasSpokenReply(type)) {
        await speakText(newAssistantText(messages, data?.messages || []));
      }
    } catch (requestError) {
      setError(errorMessage(requestError, text.sendError));
    } finally { setSubmitting(false); }
  };

  const displayedMessages = useMemo(
    () => messages.filter(
      (message) => message?.role !== 'system' && !isScenarioCoachMessage(message),
    ),
    [messages],
  );
  const completionCoachText = useMemo(() => scenarioCoachText(messages), [messages]);
  const completed = status === 'completed';
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, submitting]);
  useEffect(() => {
    axios.get(`${FASTAPI_HOST}/api/user-preferences`, authConfig())
      .then(({ data }) => setSpeechSpeed(Number(data?.photo_leafy_speech_speed) || 1))
      .catch(() => {});
    const handleBuddyUpdate = (event) => {
      if (event.detail?.voice) setBuddyVoice(event.detail.voice);
      if (event.detail?.speed) setSpeechSpeed(Number(event.detail.speed) || 1);
    };
    window.addEventListener('buddy-settings-updated', handleBuddyUpdate);
    return () => window.removeEventListener('buddy-settings-updated', handleBuddyUpdate);
  }, []);
  useEffect(() => () => stopSpeaking(), []);

  return (
    <BuddyWorkspace activity="scenario_talk" maxWidth={920} sx={{ px: 2, pt: 9, pb: { xs: 2, md: 4 } }}>
      <Box sx={{ border: '1px solid #D6DED2', borderRadius: 3, overflow: 'hidden', bgcolor: '#fff' }}>
        <WorkspaceHeader
          avatar={DEFAULT_BUDDY.image}
          avatarFallback={DEFAULT_BUDDY.reactions?.neutral || '🙂'}
          title={scenarioTitle || text.title}
          subtitle={scenarioSubtitle || text.subtitle}
          badge="Beta"
          actions={<>
            {sessionId && <Chip size="small" icon={<TheaterComedyRoundedIcon />} label={scenarioRole || text.role} />}
            <ConversationStorageControls sessionId={sessionId} storageMode={storageMode} definitions={['custom_scenario']} onRestore={applyView} onStorageModeChange={setStorageMode} currentLang={currentLang} />
          </>}
        />
        {!sessionId ? (
          <Box sx={{ minHeight: 360, display: 'grid', placeItems: 'center', p: 4, textAlign: 'center', bgcolor: '#FDFCF8' }}>
            <Stack spacing={2} alignItems="center">
              <TheaterComedyRoundedIcon sx={{ fontSize: 58, color: '#3A6752' }} />
              <Typography color="text.secondary">{text.temporary}</Typography>
              {error && <Alert severity="error">{error}</Alert>}
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="center">
                {(text.examples || []).map((example) => (
                  <Chip
                    key={example.label}
                    label={example.label}
                    clickable
                    disabled={!planChecked}
                    onClick={() => (paidAccess ? chooseExample(example) : onPurchase?.())}
                    icon={<TheaterComedyRoundedIcon />}
                    sx={{ bgcolor: '#F1F6EA', fontWeight: 700 }}
                  />
                ))}
              </Stack>
              <Button variant="contained" onClick={() => { if (!paidAccess) { onPurchase?.(); return; } setError(''); setBuilderStep('input'); setScenarioPlan(null); setBuilderOpen(true); }} disabled={!planChecked} startIcon={<AddCommentRoundedIcon />}>{text.createScenario}</Button>
              {!planChecked ? <Typography variant="caption">{text.checkingPlan}</Typography> : !paidAccess ? <Typography variant="caption">{text.paidRequired}</Typography> : null}
            </Stack>
          </Box>
        ) : <>
          <ConversationTimeline
            messages={displayedMessages}
            busy={submitting}
            endRef={endRef}
            contentRenderer={(message) => {
              if (message?.role !== 'user') return null;
              const feedback = languageFeedback[message.id];
              const hasLanguageSuggestion = Boolean(
                feedback && !feedback.already_natural && String(feedback.suggestion || '').trim(),
              );
              const visible = Boolean(visibleLanguageFeedback[message.id]);
              return (
                <Stack spacing={0.75} alignItems="flex-end">
                  <Typography component="span">{message.content}</Typography>
                  {hasLanguageSuggestion && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setVisibleLanguageFeedback((current) => ({
                        ...current, [message.id]: !current[message.id],
                      }))}
                      sx={{ minWidth: 0, p: 0, fontSize: 12, textTransform: 'none' }}
                    >
                      {text.languageFeedback}
                    </Button>
                  )}
                  {hasLanguageSuggestion && visible && (
                    <Box sx={{ width: '100%', p: 1, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.72)' }}>
                      {feedback.suggestion && (
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {text.languageSuggestion}: {feedback.suggestion}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ display: 'block', mt: feedback.suggestion ? 0.5 : 0 }}>
                        {feedback.explanation}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              );
            }}
            reactionRenderer={(message) => <Typography component="span" role="img" aria-label={`Buddy reaction: ${message.reaction || 'neutral'}`} sx={{ fontSize: 18, lineHeight: 1 }}>{DEFAULT_BUDDY.reactions?.[message.reaction] || DEFAULT_BUDDY.reactions?.neutral || '🙂'}</Typography>}
          />
          <WorkspaceComposerArea>
            {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
            {showOptions && completionCoachText && (
              <Box sx={{ mb: 1.5, p: 1.5, borderRadius: 2, bgcolor: '#F1F6EA', border: '1px solid #D6DED2' }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.25, fontWeight: 800, color: '#3A6752' }}>
                  {text.coach}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{completionCoachText}</Typography>
              </Box>
            )}
            {!completed ? <>
              {showOptions && coachGuidance && (
                <Box sx={{ mb: 1.5, p: 1.5, borderRadius: 2, bgcolor: '#F1F6EA', border: '1px solid #D6DED2' }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.25, fontWeight: 800, color: '#3A6752' }}>
                    {text.coach}
                  </Typography>
                  <Typography variant="body2">{coachGuidance}</Typography>
                </Box>
              )}
              <ConversationScaffoldAids
                choices={choices}
                hint={hint}
                visible={showOptions}
                disabled={submitting}
                language={conversationLanguage}
                onSelect={(value) => sendAction('select_choice', value)}
                onSpeak={speakText}
                onSkip={(label) => sendAction('skip_question', label)}
                trailingActions={<>
                  <FormControlLabel
                    control={<Switch size="small" checked={showOptions} disabled={submitting} onChange={(event) => { const enabled = event.target.checked; setShowOptions(enabled); if (sessionId) sendAction('set_coach_enabled', enabled); }} />}
                    label={showOptions ? text.optionsOn : text.optionsOff}
                    sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 13, fontWeight: 700 } }}
                  />
                  <Tooltip title={voiceEnabled ? text.voiceOn : text.voiceOff}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={!sessionId}
                        onClick={async () => {
                          if (voiceEnabled) { setVoiceEnabled(false); stopSpeaking(); }
                          else { setVoiceEnabled(true); await speakText(latestAssistantText()); }
                        }}
                        sx={{ border: '1px solid #d1d5db', color: voiceEnabled ? '#2f7d32' : '#6b7280' }}
                      >
                        {voicePlaying ? <CircularProgress size={20} /> : voiceEnabled ? <VolumeUpRoundedIcon /> : <VolumeOffRoundedIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </>}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField fullWidth autoComplete="off" value={answer} disabled={submitting} placeholder={text.placeholder} onChange={(event) => setAnswer(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey && answer.trim()) { event.preventDefault(); sendAction('submit_text', answer.trim()); } }} InputProps={{ endAdornment: <InputAdornment position="end"><MicTranscribeButton insideInput currentLang={conversationLanguage} followUiLanguage disabled={submitting} audioOutputActive={voicePlaying} onTranscript={(value) => setAnswer(String(value || '').trim())} onError={setError} onRecordingStart={stopSpeaking} /></InputAdornment> }} />
                <Button variant="contained" endIcon={<SendRoundedIcon />} disabled={!answer.trim() || submitting} onClick={() => sendAction('submit_text', answer.trim())}>{text.send}</Button>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <EndConversationControl label={text.end} disabled={submitting} onEnd={(label) => sendAction('end_conversation', label)} />
              </Stack>
            </> : (
              <Stack spacing={2}>
                {practiceSummary && (
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#F1F6EA', border: '1px solid #D6DED2' }}>
                    <Stack spacing={1}>
                      {(practiceSummary.practised_skills || []).length > 0 && (
                        <Box>
                          <Typography sx={{ mb: 1, fontWeight: 800 }}>{text.summaryTitle}</Typography>
                          <Stack spacing={1}>
                            {(practiceSummary.practised_skills || []).map((item) => (
                              <Box key={item.goal_id}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>✓ {item.description}</Typography>
                                {item.evidence && (
                                  <Typography variant="caption" color="text.secondary">
                                    {text.summaryEvidence}: “{item.evidence}”
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      )}
                      {(practiceSummary.next_steps || []).length > 0 && (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{text.summaryNext}</Typography>
                          {(practiceSummary.next_steps || []).map((item) => (
                            <Typography key={item.goal_id} variant="body2">• {item.description}</Typography>
                          ))}
                        </Box>
                      )}
                    </Stack>
                  </Box>
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                  variant="contained"
                  startIcon={building ? <CircularProgress size={18} color="inherit" /> : <ReplayRoundedIcon />}
                  onClick={restartScenario}
                  disabled={building}
                >
                  {building ? text.building : text.restart}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AddCommentRoundedIcon />}
                  onClick={() => { setError(''); setBuilderStep('input'); setScenarioPlan(null); setBuilderOpen(true); }}
                >
                  {text.createScenario}
                </Button>
                </Stack>
              </Stack>
            )}
          </WorkspaceComposerArea>
        </>}
      </Box>
      <Dialog open={builderOpen} onClose={() => !building && setBuilderOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>{builderStep === 'review' ? text.reviewTitle : text.createTitle}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">{builderStep === 'review' ? text.reviewIntro : text.temporaryCustom}</Alert>
            {error && <Alert severity="error">{error}</Alert>}
            {builderStep === 'review' && reviewDraft ? <>
              <TextField
                select fullWidth label={text.conversationLanguage}
                value={reviewDraft.language || roleLanguage || 'English'}
                onChange={(event) => setReviewDraft((current) => ({ ...current, language: event.target.value }))}
              >
                {STORY_LANGUAGES.map((language) => (
                  <MenuItem key={language.value} value={language.value}>{language.label}</MenuItem>
                ))}
              </TextField>
              <TextField fullWidth required label={text.title} value={reviewDraft.title} onChange={(event) => setReviewDraft((current) => ({ ...current, title: event.target.value }))} inputProps={{ maxLength: 120 }} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField fullWidth required label={text.setting} value={reviewDraft.setting} onChange={(event) => setReviewDraft((current) => ({ ...current, setting: event.target.value }))} inputProps={{ maxLength: 300 }} />
                <TextField fullWidth required label={text.objective} value={reviewDraft.objective} onChange={(event) => setReviewDraft((current) => ({ ...current, objective: event.target.value }))} inputProps={{ maxLength: 300 }} />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField fullWidth required label={text.yourRole} value={reviewDraft.participant_role} onChange={(event) => setReviewDraft((current) => ({ ...current, participant_role: event.target.value }))} inputProps={{ maxLength: 80 }} />
                <TextField fullWidth required label={text.buddyRole} value={reviewDraft.assistant_role} onChange={(event) => setReviewDraft((current) => ({ ...current, assistant_role: event.target.value }))} inputProps={{ maxLength: 80 }} />
              </Stack>
              <TextField fullWidth required multiline minRows={2} label={text.opening} value={reviewDraft.opening_message} onChange={(event) => setReviewDraft((current) => ({ ...current, opening_message: event.target.value }))} inputProps={{ maxLength: 500 }} />
              <TextField fullWidth label={text.hint} value={reviewDraft.fallback_hint} onChange={(event) => setReviewDraft((current) => ({ ...current, fallback_hint: event.target.value }))} inputProps={{ maxLength: 240 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Typography sx={{ fontWeight: 800 }}>{text.reviewGoals}</Typography>
                <Button
                  size="small" variant="outlined" startIcon={<AddRoundedIcon />}
                  disabled={(reviewDraft.goals || []).length >= 6}
                  onClick={addReviewGoal}
                >
                  {text.addGoal}
                </Button>
              </Stack>
              {(reviewDraft.goals || []).map((goal, index) => (
                <Stack key={goal.id || index} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                  <TextField
                    fullWidth required value={goal.description || ''} placeholder={text.goalPlaceholder}
                    onChange={(event) => setReviewDraft((current) => ({
                      ...current,
                      goals: current.goals.map((item, itemIndex) => itemIndex === index ? { ...item, description: event.target.value } : item),
                    }))}
                    inputProps={{ maxLength: 240 }}
                  />
                  <TextField
                    select value={goal.importance || 'helpful'} sx={{ minWidth: 125 }}
                    onChange={(event) => setReviewDraft((current) => ({
                      ...current,
                      goals: current.goals.map((item, itemIndex) => itemIndex === index ? { ...item, importance: event.target.value } : item),
                    }))}
                  >
                    <MenuItem value="core">{text.goalCore}</MenuItem>
                    <MenuItem value="helpful">{text.goalHelpful}</MenuItem>
                    <MenuItem value="optional">{text.goalOptional}</MenuItem>
                  </TextField>
                  <IconButton
                    aria-label={text.removeGoal}
                    disabled={(reviewDraft.goals || []).length <= 1}
                    onClick={() => removeReviewGoal(index)}
                    sx={{ mt: 0.5 }}
                  >
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                </Stack>
              ))}
              {!(reviewDraft.goals || []).some((goal) => goal.importance === 'core') && (
                <Alert severity="warning">{text.coreGoalRequired}</Alert>
              )}
              <Alert severity={scenarioPlan?.capability_gaps?.length > 0 ? 'warning' : 'info'}>
                <Typography variant="body2">{text.capabilityHelp}</Typography>
              </Alert>
            </> : <>
            <TextField required multiline minRows={3} label={text.describe} placeholder={text.describePlaceholder} value={scenarioForm.description} onChange={(event) => setScenarioForm((current) => ({ ...current, description: event.target.value }))} inputProps={{ maxLength: 2000 }} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth label={text.setting} value={scenarioForm.setting} onChange={(event) => setScenarioForm((current) => ({ ...current, setting: event.target.value }))} inputProps={{ maxLength: 300 }} />
              <TextField fullWidth label={text.objective} value={scenarioForm.objective} onChange={(event) => setScenarioForm((current) => ({ ...current, objective: event.target.value }))} inputProps={{ maxLength: 300 }} />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              {/* Audience is temporarily fixed to the backend default: child.
              <TextField select fullWidth label={text.audience} value={scenarioForm.audience} onChange={(event) => setScenarioForm((current) => ({ ...current, audience: event.target.value }))}>
                <MenuItem value="child">{text.child}</MenuItem><MenuItem value="parent">{text.parent}</MenuItem><MenuItem value="general">{text.general}</MenuItem>
              </TextField>
              */}
              {/* Support level is temporarily hidden until its backend behavior is defined.
              <TextField select fullWidth label={text.supportLevel} value={scenarioForm.supportLevel} onChange={(event) => setScenarioForm((current) => ({ ...current, supportLevel: event.target.value }))}>
                <MenuItem value="independent">{text.independent}</MenuItem><MenuItem value="supported">{text.supported}</MenuItem><MenuItem value="high_support">{text.highSupport}</MenuItem>
              </TextField>
              */}
              {/* Minimum turns is temporarily fixed at 3.
              <TextField type="number" fullWidth label={text.minimumTurns} value={scenarioForm.minimumTurns} onChange={(event) => setScenarioForm((current) => ({ ...current, minimumTurns: event.target.value }))} inputProps={{ min: 1, max: 10 }} />
              */}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth label={text.yourRole} value={scenarioForm.participantRole} onChange={(event) => setScenarioForm((current) => ({ ...current, participantRole: event.target.value }))} inputProps={{ maxLength: 80 }} />
              <TextField fullWidth label={text.buddyRole} value={scenarioForm.assistantRole} onChange={(event) => setScenarioForm((current) => ({ ...current, assistantRole: event.target.value }))} inputProps={{ maxLength: 80 }} />
            </Stack>
            <TextField multiline minRows={3} label={text.referenceDetails} placeholder={text.referencePlaceholder} value={scenarioForm.referenceDetails} onChange={(event) => setScenarioForm((current) => ({ ...current, referenceDetails: event.target.value }))} inputProps={{ maxLength: 12000 }} />
            <input
              ref={referenceInputRef}
              hidden
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              onChange={(event) => {
                const selected = Array.from(event.target.files || []);
                setReferenceFiles((current) => [...current, ...selected].slice(0, 5));
                event.target.value = '';
              }}
            />
            {/* <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button
                  variant="outlined"
                  startIcon={<AddPhotoAlternateRoundedIcon />}
                  onClick={() => referenceInputRef.current?.click()}
                  disabled={building || referenceFiles.length >= 5}
                >
                  {text.addReferences}
                </Button>
                <Chip icon={<VideocamOutlinedIcon />} label={text.videoLater} variant="outlined" />
              </Stack>
              <Typography variant="caption" color="text.secondary">{text.referencesHelp}</Typography>
              {referenceFiles.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {referenceFiles.map((file, index) => (
                    <Chip
                      key={`${file.name}-${file.lastModified}-${index}`}
                      label={file.name}
                      onDelete={() => setReferenceFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                      deleteIcon={<span aria-label={text.removeReference}>×</span>}
                    />
                  ))}
                </Stack>
              )}
            </Stack>*/}
            </>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          {builderStep === 'review' && <Button onClick={() => { setError(''); setBuilderStep('input'); }} disabled={building}>{text.cancel}</Button>}
          {builderStep !== 'review' && <Button onClick={() => setBuilderOpen(false)} disabled={building}>{text.cancel}</Button>}
          <Button
            variant="contained"
            onClick={builderStep === 'review' ? startReviewedScenario : planCustomScenario}
            disabled={
              building
              || (builderStep === 'review'
                ? (!reviewDraft?.title?.trim() || !reviewDraft?.setting?.trim() || !reviewDraft?.objective?.trim()
                  || !(reviewDraft?.goals || []).length
                  || !(reviewDraft?.goals || []).every((goal) => goal.description?.trim())
                  || !(reviewDraft?.goals || []).some((goal) => goal.importance === 'core'))
                : !scenarioForm.description.trim())
            }
            startIcon={building ? <CircularProgress size={18} color="inherit" /> : <AddCommentRoundedIcon />}
          >
            {building ? text.building : builderStep === 'review' ? text.startPractice : text.planScenario}
          </Button>
        </DialogActions>
      </Dialog>
    </BuddyWorkspace>
  );
}
